import { makeStyles } from '@material-ui/styles'
import GetDialogButton from 'app/plugins/openstack/components/vms/vm-details/GetDailogButton'
import { ArrayElement } from 'core/actions/Action'
import { clientActions } from 'core/client/clientReducers'
import DocumentMeta from 'core/components/DocumentMeta'
import Button from 'core/elements/button/Button'
import { BatchActionDropdown } from 'core/elements/grid/GridToolbar'
import { GridBatchActionGroupProps } from 'core/elements/grid/hooks/useGridSelectableRows'
import { HeaderPrimaryActionPortal, HeaderRefreshPortal } from 'core/elements/header/portals'
import Tabs from 'core/elements/tabs'
import Tab from 'core/elements/tabs/Tab'
import useListAction from 'core/hooks/useListAction'
import useUpdateAction from 'core/hooks/useUpdateAction'
import Theme from 'core/themes/model'
import { isReadonlyUser } from 'core/utils/helpers'
import { routeManager } from 'core/utils/routes'
import { getBlueprints } from 'openstack/components/infrastructure/actions'
import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import EditVolumeTypeModal from '../EditVolumeTypeModal'
import { listVolumeTypes } from '../actions'
import { volumeTypesSelector } from '../selectors'
import { listNonPaginatedVolumes } from '../../volumes/actions'
import { nonPaginatedVolumesSelector } from '../../volumes/selectors'
import { IVolumeTypeDetailsPageTabs } from './model'
import Overview from './Overview'

type SelectorModel = ArrayElement<ReturnType<typeof volumeTypesSelector>>

const useStyles = makeStyles<Theme>((theme) => ({
  grid: {
    display: 'grid',
    gap: 32,
  },
  actions: {
    display: 'grid',
    gridAutoColumns: 'auto max-content',
    gridAutoFlow: 'column',
    gap: 16,
  },
  dropdownAction: {
    background: theme.components.dropdown.background,
    border: 'none',
    '& .button-text': {
      color: theme.components.dropdown.color,
      justifyContent: 'flex-start',
    },
  },
}))

const VolumeTypeDetailsPage = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const { match } = useReactRouter()
  const { id } = match.params

  const { loading, reload } = useListAction(listVolumeTypes)
  const data = useSelector(volumeTypesSelector)
  const volumeType = useMemo(() => {
    return data.find((type) => type.id === id)
  }, [data, id])

  const { loading: loadingVolumes, reload: reloadVolumes } = useListAction(listNonPaginatedVolumes)
  const volumes = useSelector(nonPaginatedVolumesSelector)
  const filteredVolumes = useMemo(() => {
    return volumes.filter((volume) => {
      return volume?.volume_type === volumeType?.name
    })
  }, [volumes, volumeType])

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        id: volumeType?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [volumeType?.name, id])

  const dropdownBatchActions: GridBatchActionGroupProps<SelectorModel>[] = useMemo(
    () => [
      {
        icon: '',
        label: 'Actions',
        actions: [
          {
            key: 'Edit',
            label: 'Edit',
            icon: 'edit',
            disabled: false,
            BatchActionButton: GetDialogButton<SelectorModel, any>(
              EditVolumeTypeModal,
              { rows: [volumeType] },
              {
                className: classes.dropdownAction,
              },
            ),
          },
        ],
      },
    ],
    [classes, volumeType],
  )

  return (
    <>
      <DocumentMeta title="Volume Type Details" breadcrumbs />
      <HeaderRefreshPortal>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => {
            reload(true)
            reloadVolumes(true)
          }}
          loading={loading || loadingVolumes}
        >
          Refresh
        </Button>
      </HeaderRefreshPortal>
      {!isReadonlyUser() && (
        <HeaderPrimaryActionPortal>
          <>
            {dropdownBatchActions.map(({ label, actions }) => (
              <BatchActionDropdown<SelectorModel>
                renderOutsideGrid
                icon=""
                key={label}
                label={label}
                actions={actions}
              />
            ))}
          </>
        </HeaderPrimaryActionPortal>
      )}
      <Tabs route={routeManager.routes.openstack.volumeDetails}>
        <Tab label="Overview" value={IVolumeTypeDetailsPageTabs.Overview}>
          <Overview
            volumeType={volumeType}
            loading={loading}
            loadingVolumes={loadingVolumes}
            volumes={filteredVolumes}
          />
        </Tab>
      </Tabs>
    </>
  )
}

export default VolumeTypeDetailsPage
